import { Button, Page } from "@rentiohq/web-shared/dist/components";
import React from "react";
import {
  ACADEMY_BASE_PATH,
  ACADEMY_CHAPTERS,
  ACADEMY_SLIDE_PARAM,
  ACADEMY_SLIDES,
} from "../../RentmatchAcademy.constants";
import { useAcademySearchQuery } from "../../RentmatchAcademy.hooks";
import {
  getAcademyText,
  getSlideKey,
  getUiKey,
} from "../../RentmatchAcademy.utils";
import { AcademyHighlight } from "../AcademyHighlight";
import { AcademyMedia } from "../AcademyMedia";
import { AcademyText } from "../AcademyText";
import * as S from "./AcademySlides.styles";

const LAST_INDEX = ACADEMY_SLIDES.length - 1;

// Arrow keys belong to these elements: a video seeks with them, a field moves
// its cursor. Only a key press on the page itself pages the slides.
const KEYBOARD_OWNING_TAGS = ["INPUT", "TEXTAREA", "SELECT", "VIDEO"];

const isSlideKeyPress = (event: KeyboardEvent) => {
  if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
    return false;
  }
  const target = event.target as HTMLElement | null;
  if (!target) {
    return true;
  }

  return (
    !KEYBOARD_OWNING_TAGS.includes(target.tagName) && !target.isContentEditable
  );
};

export const AcademySlides = () => {
  const { query, searchParams } = useAcademySearchQuery();
  // A search result opens the short version at the slide it found, with the
  // search marked on that slide.
  const targetIndex = ACADEMY_SLIDES.findIndex(
    item => item.id === searchParams.get(ACADEMY_SLIDE_PARAM),
  );

  // State
  const [index, setIndex] = React.useState(Math.max(0, targetIndex));

  const slide = ACADEMY_SLIDES[index];
  const slideQuery = index === targetIndex ? query : undefined;
  const isFirst = index === 0;
  const isLast = index === LAST_INDEX;

  // Event handlers
  const handlePrevious = () => setIndex(current => Math.max(0, current - 1));

  const handleNext = () =>
    setIndex(current => Math.min(LAST_INDEX, current + 1));

  // Lifecycle
  React.useEffect(() => {
    // The arrow keys page through the slides, like a presentation.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isSlideKeyPress(event)) {
        return;
      }
      if (event.key === "ArrowLeft") {
        handlePrevious();
      }
      if (event.key === "ArrowRight") {
        handleNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Render
  const renderControls = () => (
    <S.Controls>
      <Button
        appearance="outline"
        isDisabled={isFirst}
        onClick={handlePrevious}
      >
        {getAcademyText("system.previous")}
      </Button>
      <S.Counter>
        {getAcademyText(getUiKey("slides.counter"), {
          current: `${index + 1}`,
          total: `${ACADEMY_SLIDES.length}`,
        })}
      </S.Counter>
      {isLast ? (
        <Button
          appearance="primary"
          url={`${ACADEMY_BASE_PATH}/${ACADEMY_CHAPTERS[0].id}`}
        >
          {getAcademyText(getUiKey("slides.to_full"))}
        </Button>
      ) : (
        <Button appearance="primary" onClick={handleNext}>
          {getAcademyText("system.next")}
        </Button>
      )}
    </S.Controls>
  );

  const title = getAcademyText(getSlideKey(slide.id, "title"));

  return (
    <Page
      title={getAcademyText(getUiKey("short.title"))}
      breadcrumbs={{
        to: ACADEMY_BASE_PATH,
        content: getAcademyText("system.back_to_overview"),
      }}
    >
      <S.Slide hasMedia={!!slide.media}>
        <S.SlideText>
          <S.SlideLabel>
            <AcademyHighlight
              text={getAcademyText(getSlideKey(slide.id, "label"))}
              query={slideQuery}
            />
          </S.SlideLabel>
          <S.SlideTitle>
            <AcademyHighlight text={title} query={slideQuery} />
          </S.SlideTitle>
          <S.SlideBody>
            <AcademyText
              text={getAcademyText(getSlideKey(slide.id, "body"))}
              query={slideQuery}
            />
          </S.SlideBody>
        </S.SlideText>
        {slide.media && (
          <S.SlideMedia>
            <AcademyMedia key={slide.id} mediaId={slide.media} alt={title} />
          </S.SlideMedia>
        )}
      </S.Slide>
      {renderControls()}
    </Page>
  );
};
